import { useSnapshot } from 'valtio';

import Link from '@cartwhl/common/components/Link';
import Text from '@cartwhl/ui/base/Text';
import ArrowUpRightIcon from '@cartwhl/ui/icons/ArrowUpRightIcon';
import DiscordIcon from '@cartwhl/ui/icons/DiscordIcon';
import Button from '@cartwhl/ui/input/Button';
import Panel from '@cartwhl/ui/layout/Panel';

import { uiStore } from 'stores';

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div>
      <Text
        variant="studio/body/medium/sm"
        as="h2"
        className="mb-2 flex h-[34px] items-center"
      >
        {title}
      </Text>
      <div className="border-gray-30 dark:border-gray-60 dark:bg-gray-90 rounded-2xl border bg-white px-4 pt-6 pb-4">
        {children}
      </div>
    </div>
  );
}

function SectionButton({
  href,
  children,
  variant = 'secondary',
  maxWidth = '200px',
  icon,
}: {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  maxWidth?: string;
  icon?: React.ReactElement;
}) {
  return (
    <div className="flex justify-end">
      <Link href={href} target="_blank">
        <Button
          className={`max-w-[${maxWidth}] justify-center`}
          variant={variant}
          icon={variant === 'secondary' ? <ArrowUpRightIcon /> : icon}
          alignIcon={variant === 'secondary' ? 'end' : 'start'}
        >
          {children}
        </Button>
      </Link>
    </div>
  );
}

interface TipRowProps {
  imageSrc: string;
  title: string;
  description: string;
}

function TipRow({ imageSrc, title, description }: TipRowProps) {
  return (
    <div className="flex flex-row items-start gap-4">
      <img src={imageSrc} alt={title} className="ml-4 h-16 w-16 rounded-lg" />
      <div className="flex flex-col gap-2">
        <Text variant="studio/body/bold/md">{title}</Text>
        <Text variant="studio/body/medium/sm">{description}</Text>
      </div>
    </div>
  );
}

export default function GetStartedPanel({
  close,
  isOpen,
  enterTransitionDuration = 0,
  exitTransitionDuration = 0,
}: {
  close: () => void;
  isOpen?: boolean;
  enterTransitionDuration?: number;
  exitTransitionDuration?: number;
}) {
  const { subPanelKey } = useSnapshot(uiStore.state);
  return (
    <Panel
      title="Get Started"
      isOpen={isOpen}
      closePanel={close}
      subPanelKey={subPanelKey}
      enterTransitionDuration={enterTransitionDuration}
      exitTransitionDuration={exitTransitionDuration}
    >
      <div className="flex flex-col gap-6">
        <Section title="Prompt guide">
          <div className="flex flex-col">
            <img
              className="rounded-lg pb-6"
              src="/images/welcome/3.webp"
              alt="Prompt guide"
            />
            <Text variant="studio/body/medium/sm" as="p" className="">
              Prompts are short statements that our model will interpret to
              generate a 3D character animation. Prompts that yield the best
              results should be simple and direct. They should briefly state the{' '}
              <strong className="text-blue">action</strong> with an optional{' '}
              <strong className="text-purple-700">modifier</strong>. For
              example:
            </Text>
            <Text
              variant="studio/body/medium/sm"
              as="ul"
              className="list-outside list-disc py-1 pl-6"
            >
              <li>
                "<strong className="text-blue">Kick</strong>{' '}
                <strong className="text-purple-700">like a ninja</strong>"
              </li>
              <li>
                "<strong className="text-blue">Swing</strong>{' '}
                <strong className="text-purple-700">a baseball bat</strong>"
              </li>
              <li>
                "<strong className="text-blue">Punch</strong>"
              </li>
            </Text>
            <SectionButton href="https://docs.getcartwheel.com/docs/creating-motion/prompt-introduction">
              Learn more
            </SectionButton>
          </div>
        </Section>

        <Section title="Download example motions & trial rig">
          <div className="flex flex-col gap-6">
            <img
              className="rounded-lg"
              src="/images/get_started/lin_kick.webp"
              alt="Prompt guide"
            />
            <Text variant="studio/body/medium/sm" as="p" className="">
              Want to test our animations or evaluate our rig before becoming a
              subscriber? Download example motions and test our trial rig for
              free!
            </Text>
            <SectionButton
              href="https://docs.getcartwheel.com/docs/integrating-cartwheel/integration"
              maxWidth="400px"
            >
              Get the motions & trial rig
            </SectionButton>
          </div>
        </Section>

        <Section title="More helpful tips">
          <div className="flex flex-col gap-12">
            <TipRow
              imageSrc="/images/get_started/settings.webp"
              title="Cartwheel features"
              description="Get familiar with Cartwheel's feature set."
            />
            <TipRow
              imageSrc="/images/get_started/favorite.webp"
              title="Favoriting & finding motions"
              description="Learn about favoriting and finding previous generations."
            />
            <TipRow
              imageSrc="/images/get_started/share.webp"
              title="Share with us!"
              description="Make something cool? We'd love to see it!"
            />
            <SectionButton href="https://docs.getcartwheel.com/">
              View documentation
            </SectionButton>
          </div>
        </Section>

        <Section title="Join our Discord">
          <div className="flex flex-col gap-6">
            <img
              className="rounded-lg"
              src="/images/get_started/discord_invite.webp"
              alt="Prompt guide"
            />
            <Text variant="studio/body/medium/sm" as="p" className="">
              Want to give us feedback or request a feature? Join our discord!
              Join our community to talk to other users or talk directly with
              the Cartwheel team.
            </Text>
            <SectionButton
              href="https://discord.com/invite/TQS278evHH"
              variant="primary"
              icon={<DiscordIcon />}
            >
              Join discord
            </SectionButton>
          </div>
        </Section>
      </div>
    </Panel>
  );
}
